import { useState, useEffect, ReactNode, useMemo } from 'react';
import { AlarmClock, Clock, Eye, EyeOff, Lock, Mail, ScrollText, ShieldAlert, Shield, TestTube2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAdminAuth, getAdminActivityLog, logAdminActivity } from '@/hooks/useAdminAuth';
import { supabase } from '@/integrations/supabase/client';
import { useSessionTimeout, formatTimeoutCountdown } from '@/hooks/useSessionTimeout';
import {
  TEST_ADMIN_ABSOLUTE_MS,
  TEST_ADMIN_IDLE_MS,
  TEST_ADMIN_WARN_MS,
} from '@/config/admin-session';
import {
  clearTestAdminSession,
  isTestAdminEmail,
  readTestAdminSession,
} from '@/utils/test-admin-session';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import OcrHealthAlertBanner from '@/components/admin/OcrHealthAlertBanner';
import AdminBuildStatusBanner from '@/components/admin/AdminBuildStatusBanner';

interface AdminAuthProps {
  children: ReactNode;
}

interface ActivityLogEntry {
  id: string;
  user_id: string;
  action: string;
  details: string;
  created_at: string;
}

export const AdminAuth = ({ children }: AdminAuthProps) => {
  const {
    isAuthenticated,
    isAdmin,
    isLoading,
    userEmail,
    userId,
    signIn,
    signOut,
  } = useAdminAuth();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  // Detect a sandbox (test-admin) session. We trust EITHER the explicit
  // marker written by /admin/test-login OR the canonical email — the
  // double-check guards against the marker being lost across reloads.
  const testMarker = useMemo(() => readTestAdminSession(), [isAuthenticated, userEmail]);
  const isTestAdmin = Boolean(testMarker) || isTestAdminEmail(userEmail);

  // Anchor the absolute cap to the marker timestamp when we have one (so
  // the timer survives a reload), otherwise to "now". useMemo with the
  // marker as a dependency keeps the value stable for the hook.
  const sessionStart = useMemo(
    () => (testMarker?.mintedAt ?? Date.now()),
    [testMarker],
  );

  const handleAutoLogout = async (reason: 'inactivity' | 'absolute') => {
    // Best-effort audit entry — fire-and-forget so we never block the
    // sign-out path on a network error.
    if (userId) {
      logAdminActivity(
        userId,
        'test_session_timeout',
        JSON.stringify({ v: 1, reason, idle_ms: TEST_ADMIN_IDLE_MS, absolute_ms: TEST_ADMIN_ABSOLUTE_MS }),
      ).catch(() => undefined);
    }
    clearTestAdminSession();
    toast({
      title: 'Sandbox session ended',
      description: reason === 'inactivity'
        ? 'You were signed out after 24 hours of inactivity.'
        : 'The 48-hour sandbox session limit was reached.',
      variant: 'destructive',
    });
    await signOut();
  };

  const timeout = useSessionTimeout({
    enabled: isAuthenticated && isAdmin && isTestAdmin,
    idleMs: TEST_ADMIN_IDLE_MS,
    absoluteMs: TEST_ADMIN_ABSOLUTE_MS,
    warnBeforeMs: TEST_ADMIN_WARN_MS,
    sessionStart,
    onTimeout: handleAutoLogout,
  });

  // If the user logs out (or the session is dropped server-side), drop the
  // sandbox marker so it can't bleed across logins.
  useEffect(() => {
    if (!isAuthenticated) clearTestAdminSession();
  }, [isAuthenticated]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await signIn(email, password);
    } finally {
      setIsSubmitting(false);
      setEmail('');
      setPassword('');
    }
  };

  const handleSendReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sendingReset) return;
    const target = forgotEmail.trim();
    if (!target) return;
    setSendingReset(true);
    const { error } = await supabase.auth.resetPasswordForEmail(target, {
      redirectTo: `${window.location.origin}/admin/reset-password`,
    });
    setSendingReset(false);
    // Always show a generic confirmation to avoid leaking which emails are admins.
    toast({
      title: 'Reset link sent',
      description: 'If that email belongs to an admin, a reset link is on the way.',
    });
    if (!error) {
      setShowForgot(false);
      setForgotEmail('');
    }
  };

  const handleLogout = async () => {
    clearTestAdminSession();
    await signOut();
  };

  const handleOpenActivityLog = async () => {
    const log = await getAdminActivityLog();
    setActivityLog(log);
    setShowActivityLog(true);
  };

  const formatLogDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      login: 'Login',
      logout: 'Logout',
      exclude_artifact: 'Excluded Artifact',
      restore_artifact: 'Restored Artifact',
      add_user: 'Added User',
      delete_user: 'Deleted User',
      change_password: 'Changed Password',
    };
    return labels[action] || action;
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-background flex items-center justify-center"
        role="status"
        aria-live="polite"
      >
        <span className="sr-only">Verifying admin access…</span>
        <div className="h-10 w-10 rounded-full border-2 border-muted border-t-primary animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="font-display text-2xl">Admin Access</CardTitle>
            <CardDescription>
              Sign in with your admin credentials.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  autoFocus
                  required
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>
            <p className="text-xs text-muted-foreground text-center mt-4">
              Need to set up the first admin? <Link to="/admin/setup" className="text-primary hover:underline">Start setup</Link>
            </p>
            <p className="text-xs text-muted-foreground text-center mt-2">
              <button
                type="button"
                onClick={() => { setForgotEmail(email); setShowForgot(true); }}
                className="text-primary hover:underline"
              >
                Forgot password?
              </button>
            </p>
          </CardContent>
        </Card>
        <Dialog open={showForgot} onOpenChange={setShowForgot}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset admin password</DialogTitle>
              <DialogDescription>
                Enter your admin email. We'll send a secure link to set a new password.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSendReset} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="admin@example.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="pl-10"
                  required
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={sendingReset}>
                {sendingReset ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div>
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        {isTestAdmin && (
          <div
            className={cn(
              'flex items-center gap-1.5 rounded border px-2 py-1 text-xs backdrop-blur-sm transition-colors',
              timeout.isWarning
                ? 'border-destructive/60 bg-destructive/15 text-destructive animate-pulse'
                : timeout.secondsRemaining <= 5 * 60
                  ? 'border-primary/60 bg-primary/10 text-foreground'
                  : 'border-border bg-background/80 text-muted-foreground',
            )}
            title={`Sandbox session — auto-logout in ${formatTimeoutCountdown(timeout.secondsRemaining)} (reason: ${timeout.activeReason})`}
            role="timer"
            aria-live="polite"
          >
            {timeout.isWarning ? (
              <ShieldAlert className="h-3 w-3" />
            ) : (
              <AlarmClock className="h-3 w-3" />
            )}
            <span className="font-mono tabular-nums">
              {formatTimeoutCountdown(timeout.secondsRemaining)}
            </span>
            <span className="hidden sm:inline">
              {timeout.activeReason === 'inactivity' ? 'idle' : 'session'}
            </span>
          </div>
        )}
        {isTestAdmin && (
          <div
            className="hidden items-center gap-1.5 rounded border border-primary/40 bg-primary/10 px-2 py-1 text-xs font-medium text-primary md:inline-flex"
            title="This is a sandbox seat minted via /admin/test-login"
          >
            <TestTube2 className="h-3 w-3" /> Sandbox
          </div>
        )}
        <Dialog open={showActivityLog} onOpenChange={setShowActivityLog}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" onClick={handleOpenActivityLog}>
              <ScrollText className="h-4 w-4 mr-2" />
              Activity Log
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Activity Log</DialogTitle>
              <DialogDescription>
                Recent admin activity and changes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {activityLog.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">No activity recorded yet.</p>
              ) : (
                activityLog.map((entry) => (
                  <div key={entry.id} className="p-3 bg-muted/50 rounded-md border border-border">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-primary">{getActionLabel(entry.action)}</span>
                      <span className="text-xs text-muted-foreground">{formatLogDate(entry.created_at)}</span>
                    </div>
                    <p className="text-sm text-foreground">{entry.details}</p>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
        <div className="text-xs text-muted-foreground bg-background/80 backdrop-blur-sm px-2 py-1 rounded border">
          {userEmail}
        </div>
        <Button variant="outline" size="sm" onClick={handleLogout}>
          Logout
        </Button>
      </div>
      <OcrHealthAlertBanner />
      <AdminBuildStatusBanner />
      {children}

      {/* Inactivity warning modal — only fires for sandbox seats. */}
      <AlertDialog open={isTestAdmin && timeout.isWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" aria-hidden />
              Session about to expire
            </AlertDialogTitle>
            <AlertDialogDescription>
              {timeout.activeReason === 'inactivity'
                ? 'No activity has been detected on this sandbox seat.'
                : 'The 48-hour hard cap for sandbox sessions is almost up.'}{' '}
              You will be signed out in{' '}
              <span className="font-mono font-semibold text-destructive tabular-nums">
                {formatTimeoutCountdown(timeout.secondsRemaining)}
              </span>
              .{' '}
              {timeout.activeReason === 'inactivity'
                ? 'Press “Stay signed in” to extend.'
                : 'A fresh sign-in will be required to continue.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleLogout()}>
              Log out now
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => timeout.extendSession()}
              disabled={timeout.activeReason === 'absolute'}
              title={timeout.activeReason === 'absolute' ? 'Absolute session cap cannot be extended.' : undefined}
            >
              <Clock className="mr-2 h-4 w-4" /> Stay signed in
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Export for use in other components
export { logAdminActivity };
